/* ============================================================
   MOSAIK — LES OBJETS POSÉS SUR LE PLAN  (glisser-déposer)

   Une couche d'OBJETS que le visiteur fait glisser depuis la barre latérale
   jusque sur le plan. Deux familles, qui ne coûtent pas la même chose :

     - LES EMPLACEMENTS (« encadrements de voiture ») — un contour d'UNE dalle
       d'épaisseur, aux cotes d'un véhicule. Ils ne s'ajoutent pas au devis :
       ils RECOLORENT les dalles qu'ils couvrent, donc ce sont les dalles déjà
       comptées qui changent de couleur. Retirer l'encadrement rend au sol ses
       couleurs d'avant — le dessin du visiteur n'est jamais écrasé.

     - LES ACCESSOIRES (luminaires hexagonaux) — des produits vendus EN PLUS,
       posés au plafond. Ils ne touchent à aucune dalle : ils se dessinent
       par-dessus le plan, et s'ajoutent au récapitulatif et au panier.

   ------------------------------------------------------------
   POURQUOI LES DALLES NE SONT PAS REPEINTES POUR DE BON

   Un encadrement est un OBJET VIVANT : on le glisse, on le tourne, on le
   retire. S'il écrivait sa couleur dans `mosaik.result`, le retirer laisserait
   son empreinte — il faudrait mémoriser ce qu'il y avait dessous, et deux
   encadrements qui se croisent rendraient la chose insoluble.

   Le plan garde donc SES couleurs, et cette couche fournit une TABLE DE
   SUBSTITUTION (dalle -> couleur) que le rendu et le récapitulatif consultent
   au dernier moment : `mosaik.tileOverrides()`. Une dalle recouverte est
   dessinée ET FACTURÉE dans la couleur de l'encadrement (c'est bien elle que
   le client recevra), mais rien n'est perdu dessous.

   ------------------------------------------------------------
   DEUX CALQUES DE DESSIN, POUR DEUX DURÉES DE VIE

     - le PLAN (mosaik-render.js) porte ce qui fait partie de la création :
       les dalles recolorées et les luminaires. C'est donc ce qui part dans le
       PDF et dans la commande.
     - un CALQUE DE RECOUVREMENT (le même procédé que mosaik-select.js) porte
       ce qui n'est qu'une aide au geste : les poignées, le cadre de sélection
       et l'aperçu de l'objet en cours de déplacement. Redessiner le plan à
       chaque mouvement de souris est impossible (draw() est asynchrone, il
       attend les images des dalles) ; ce calque, lui, se redessine en une
       fraction de milliseconde. Il est marqué hors-PDF.

   ------------------------------------------------------------
   LE GESTE, LE MÊME PARTOUT

     - à la souris : on prend la vignette dans la barre latérale, on la lâche
       sur le plan ;
     - au doigt : le même glissé fonctionne, mais la barre latérale et le plan
       sont parfois à deux écrans l'un de l'autre — une simple TAPE sur la
       vignette l'arme donc, et la tape suivante sur le plan la pose.

   Une fois posé, un objet se reprend par n'importe quel point de son tracé
   (le contour d'un encadrement, les tubes d'un luminaire) ou par sa pastille
   centrale. L'INTÉRIEUR RESTE PEIGNABLE : on doit pouvoir colorer les dalles
   au milieu d'un emplacement sans avoir à déplacer l'objet.

   ------------------------------------------------------------
   BRANCHEMENTS (tous protégés par `window.MosaikObjects` côté appelant)
     rendu      : MosaikObjects.tileOverrides(m)  +  .draw(m, layout)
     récap      : MosaikObjects.recapRows(m)      +  .price()
     panier     : MosaikObjects.addCartItems(m, items)
     historique : MosaikObjects.serialize() / .restore(m, arr)
     clics      : MosaikObjects.blocksPaint()     (mosaik-controller.js)
     doigt      : MosaikObjects.touchBegin/Move/End (mosaik-zoom.js)
     UI         : MosaikObjects.setupUI(m)        (bootstrap)

   VISIBILITÉ : le module et son markup ne sont émis que si le drapeau
   `objects_enabled` est vrai dans templates/page.mosaik-configurator.liquid.
   ============================================================ */
window.MosaikObjects = {

  enabled: true,

  /* ---------- LE CATALOGUE ----------
     C'EST ICI QU'ON AJOUTE OU MODIFIE UN ARTICLE. Les cotes sont en MÈTRES
     (jamais en dalles) : un encadrement de berline fait 2,50 × 5,00 m quelle
     que soit la gamme posée dessous, et c'est la taille de dalle de la gamme
     qui décide du nombre de dalles que cela représente.

     PRIX ET IDENTIFIANTS DE VARIANTE : à remplir (voir products/id plus bas).
     Tant que `price` vaut 0, aucun montant n'est affiché nulle part — ni sur
     la vignette, ni dans le récapitulatif : mieux vaut ne rien annoncer qu'un
     tarif inventé. Tant que `id` est nul, l'article ne part pas au panier et
     la console le dit. */
  CATALOG: {

    /* Emplacements véhicule — contour d'une dalle d'épaisseur.
       `w` = largeur (en travers du garage), `h` = longueur (dans l'axe de
       l'entrée, qui est en bas du plan). Le bouton « tourner » échange les
       deux, pour un garage où l'on se gare en travers. */
    frames: [
      { key: 'moto', name_fr: 'Moto', name_en: 'Motorbike', w: 1.20, h: 2.50 },
      { key: 'citadine', name_fr: 'Citadine', name_en: 'City car', w: 2.30, h: 4.60 },
      { key: 'berline', name_fr: 'Berline / break', name_en: 'Saloon', w: 2.50, h: 5.00 },
      { key: 'suv', name_fr: 'SUV / 4×4', name_en: 'SUV / 4x4', w: 2.70, h: 5.50 },
      { key: 'utilitaire', name_fr: 'Utilitaire', name_en: 'Van', w: 2.90, h: 6.20 }
    ],

    /* Luminaires hexagonaux — LE NID D'ABEILLES DU PRODUIT RÉEL.

       Chaque modèle donne ses modules en CELLULES [q, r], dans le repère du nid
       d'abeilles : un pas de q vaut 3/4 de largeur d'hexagone, un pas de r une
       demi-hauteur, et q+r est toujours PAIR. C'est exactement ainsi que deux
       hexagones à plat (pointes à gauche et à droite) partagent une arête
       oblique — toute liste écrite de cette façon se referme donc comme le vrai
       luminaire, sans jeu ni recouvrement.

       Les deux dispositions sont relevées sur les photos du fabricant :
         5 hexagones  — 2 en haut, 1 au centre (décalé d'une demi-hauteur), 2 en bas
         14 hexagones — 9 rangées alternées 2/1/2/1/2/1/2/1/2, vendu avec ou
                        sans son cadre rectangulaire (`frame`).

       `w` et `h` SONT L'ENCOMBREMENT RÉEL DU PRODUIT, en mètres — la cote du
       catalogue du fabricant, et donc la place que le luminaire prend sur le
       plan. C'est d'elle que la taille d'un hexagone est DÉDUITE (voir combOf) :
       aucune cote de module n'est écrite nulle part, il n'y a donc rien qui
       puisse contredire la fiche produit. Corriger un encombrement suffit.

       `comb` n'existe que pour les modèles à cadre : c'est l'encombrement du
       nid d'abeilles SEUL, à l'intérieur. Le cadre est alors ce qui reste entre
       les deux (ici 32 cm de part et d'autre, 1,5 cm en haut et en bas).

       LA CLÉ IDENTIFIE L'ARTICLE : elle est écrite dans les projets enregistrés
       et sert à retrouver sa fiche (voir def()). Deux articles ne peuvent donc
       pas la partager — ils seraient le même produit. */
    lights: [
      { key:'hex5',   name_fr:'Plafonnier LED — 5 hexagones',
                      name_en:'LED Ceiling Light — 5 Hexagons',
        cells:[[0,0],[2,0],[1,1],[0,2],[2,2]],
        w:2.40, h:1.60, weight:3.5,
        frame:false, price:199, id:55798735012171 },
      { key:'hex14',  name_fr:'Plafonnier LED — 14 hexagones',
                      name_en:'LED Ceiling Light — 14 Hexagons',
        cells:[[0,0],[2,0],[1,1],[0,2],[2,2],[1,3],[0,4],[2,4],[1,5],[0,6],[2,6],[1,7],[0,8],[2,8]],
        w:4.20, h:2.40, weight:7,
        frame:false, price:349, id:55798735044939 },
      { key:'hex14f', name_fr:'Plafonnier LED — 14 hexagones avec cadre',
                      name_en:'LED Ceiling Light — 14 Hexagons with Frame',
        cells:[[0,0],[2,0],[1,1],[0,2],[2,2],[1,3],[0,4],[2,4],[1,5],[0,6],[2,6],[1,7],[0,8],[2,8]],
        w:4.84, h:2.43, comb:[4.20, 2.40], weight:11,
        frame:true, price:449, id:55798735077707 }
    ]
  },

  /* Le tube lumineux : une cote de PRODUIT, la seule qui ne se déduise pas de
     l'encombrement (6 cm de profilé). */
  HEX_TUBE: 0.06,

  /* ---------- ÉTAT ----------
     `items` est la source de vérité. Un objet :
       { id, family:'frame'|'light', key, col, row, rot, pile }
     col/row = coin haut-gauche, EN DALLES (fractionnaire pour un luminaire,
     qui n'a pas de raison de se caler sur le quadrillage du sol). Stocker en
     dalles plutôt qu'en pixels, c'est survivre à un zoom, un redimensionnement
     et un rechargement — exactement comme la zone de mosaik-select.js.
     `pile` = index de la couleur dans la gamme courante (encadrements). */
  items: [],
  sel: null,            // id de l'objet sélectionné
  _nextId: 1,
  pileIdx: null,        // couleur retenue pour le prochain encadrement

  m: null, cv: null, frame: null, ov: null, octx: null,

  armed: null,          // {family, key} armé par une tape, en attente du plan
  drag: null,           // geste en cours (dépose ou déplacement)
  _acted: false,        // un clic vient d'être consommé : il ne doit pas peindre

  GRIP_R: 11,           // rayon de la pastille de prise, en pixels d'écran
  HANDLE_R: 13,         // rayon des poignées « tourner » et « retirer »
  HIT_PAD: 5,           // tolérance de visée autour d'un tracé, en pixels
  DRAG_MIN: 4,          // px : en deçà, le geste est une tape, pas un glissé

  /* ============================================================
     GÉOMÉTRIE
     ============================================================ */

  /* Pixels par mètre sur chaque axe. Les gammes ont toutes des dalles carrées,
     mais rien n'oblige à ce que ça le reste : les deux axes sont donc tenus
     séparément, comme dans tout le reste du configurateur. */
  metrics: function (m) {
    const L = m.layout;
    if (!L || !(L.tileW > 0) || !(L.tileH > 0)) return null;
    return { L, mx: L.tileW / m.options.mosaikWidth, my: L.tileH / m.options.mosaikHeight };
  },

  def: function (item) {
    const list = (item.family === 'frame') ? this.CATALOG.frames : this.CATALOG.lights;
    return list.find(d => d.key === item.key) || null;
  },

  /* LE NID D'ABEILLES D'UN LUMINAIRE, en mètres, coin haut-gauche à l'origine.
     Une seule fonction produit tout ce qui décrit sa forme — les centres des
     hexagones, ses cotes hors-tout, et son cadre s'il en a un — pour que le
     dessin sur le plan, la vignette de la barre latérale et la zone de prise ne
     puissent pas diverger : les trois lisent ceci.

     Les cellules [q, r] du catalogue sont converties ici : un pas de q vaut
     3/4 de largeur d'hexagone, un pas de r une demi-hauteur (voir CATALOG). */
  combOf: function (def) {
    if (!def || !def.cells) return null;
    const qs = def.cells.map(c => c[0]), rs = def.cells.map(c => c[1]);
    const q0 = Math.min.apply(null, qs), r0 = Math.min.apply(null, rs);
    const dq = Math.max.apply(null, qs) - q0, dr = Math.max.apply(null, rs) - r0;
    /* L'HEXAGONE SE DÉDUIT DE L'ENCOMBREMENT, jamais l'inverse. Le nid
       d'abeilles mesure (dq·¾ + 1) hexagones de large et (dr·½ + 1) de haut :
       la cote du produit divisée par ce nombre donne le module. Un modèle qui
       change de taille au catalogue voit donc ses hexagones suivre, et le
       dessin ne peut pas mentir sur la place que le luminaire occupera. */
    const cw = (def.comb && def.comb[0]) || def.w || 0;
    const ch = (def.comb && def.comb[1]) || def.h || 0;
    const W = cw / (dq * 0.75 + 1), H = ch / (dr * 0.5 + 1);
    // le cadre est ce qui dépasse du nid d'abeilles, réparti sur les 4 côtés
    const padX = ((def.w || cw) - cw) / 2, padY = ((def.h || ch) - ch) / 2;
    const cells = def.cells.map(c => ({
      x: padX + W / 2 + (c[0] - q0) * 0.75 * W,
      y: padY + H / 2 + (c[1] - r0) * 0.5 * H
    }));
    return { cells, hw: W / 2, hh: H / 2,
             w: def.w || cw, h: def.h || ch, frame: !!def.frame };
  },

  /* Cotes d'un article EN MÈTRES, rotation comprise. Un luminaire tient les
     siennes de son nid d'abeilles, un encadrement les porte en propre. */
  sizeM: function (item) {
    const d = this.def(item);
    if (!d) return { w: 0, h: 0 };
    let w, h;
    if (item.family === 'frame') { w = d.w; h = d.h; }
    else {
      const c = this.combOf(d);
      if (!c) return { w: 0, h: 0 };
      w = c.w; h = c.h;
    }
    return item.rot ? { w: h, h: w } : { w, h };
  },

  /* Emprise EN DALLES. Un encadrement occupe un nombre ENTIER de dalles (son
     contour est fait de dalles, il ne peut pas en couper une en deux) et n'en
     fait jamais moins de deux par côté — un contour d'une seule dalle de large
     ne serait pas un contour. */
  sizeT: function (m, item) {
    const s = this.sizeM(item);
    let wT = s.w / m.options.mosaikWidth, hT = s.h / m.options.mosaikHeight;
    if (item.family === 'frame') {
      wT = Math.max(2, Math.round(wT));
      hT = Math.max(2, Math.round(hT));
    }
    return { wT, hT };
  },

  /* Boîte de l'objet en pixels internes du plan (ceux dans lesquels le canvas
     est dessiné et dans lesquels les clics sont convertis). */
  boxOf: function (m, item) {
    const g = this.metrics(m);
    if (!g) return null;
    const { L } = g, t = this.sizeT(m, item);
    return {
      x: L.xOff + item.col * L.tileW, y: L.yOff + item.row * L.tileH,
      w: t.wT * L.tileW, h: t.hT * L.tileH, wT: t.wT, hT: t.hT
    };
  },

  /* LE QUART DE TOUR, EN UN SEUL ENDROIT.
     Tourner le luminaire, c'est échanger les deux axes — des centres comme des
     sommets d'hexagone. Les deux dispositions sont symétriques sur leurs deux
     axes, donc cet échange donne bien le quart de tour attendu, et il suffit de
     l'appliquer partout de la même façon pour que le dessin, la vignette et la
     visée restent d'accord entre eux. */
  turn: function (rot, x, y) { return rot ? { x: y, y: x } : { x: x, y: y }; },

  /* Centres des hexagones d'un luminaire, en mètres depuis le coin haut-gauche
     de son emprise, rotation comprise. */
  hexCenters: function (item) {
    const c = this.combOf(this.def(item));
    if (!c) return [];
    const self = this;
    return c.cells.map(p => self.turn(item.rot, p.x, p.y));
  },

  /* Les six sommets d'un hexagone À PLAT (pointes à gauche et à droite), en
     mètres autour de son centre. `hw`/`hh` sont ses demi-cotes ; la rotation
     est appliquée par turn(), comme pour les centres. */
  hexPoints: function (rot, hw, hh) {
    const self = this;
    return [[hw, 0], [hw / 2, hh], [-hw / 2, hh], [-hw, 0], [-hw / 2, -hh], [hw / 2, -hh]]
      .map(p => self.turn(rot, p[0], p[1]));
  },

  /* Les dalles du CONTOUR d'un encadrement, en indices de dalle du plan.
     Hors-plan et dalles supprimées à la gomme sont écartées : l'encadrement
     n'invente pas de sol là où il n'y en a pas. */
  ringTiles: function (m, item, cols, rows) {
    const out = [];
    const t = this.sizeT(m, item);
    const c0 = Math.round(item.col), r0 = Math.round(item.row);
    for (let r = r0; r < r0 + t.hT; r++) {
      if (r < 0 || r >= rows) continue;
      for (let c = c0; c < c0 + t.wT; c++) {
        if (c < 0 || c >= cols) continue;
        const edge = (c === c0 || c === c0 + t.wT - 1 || r === r0 || r === r0 + t.hT - 1);
        if (!edge) continue;
        const idx = r * cols + c;
        if (m.result[idx] === false) continue;         // dalle retirée : rien à recolorer
        out.push(idx);
      }
    }
    return out;
  },

  /* ============================================================
     LA TABLE DE SUBSTITUTION  (lue par le rendu ET par le récapitulatif)

     C'est le seul point par lequel un encadrement touche au sol. Rien n'est
     mis en cache : quelques objets × leur contour, c'est une poignée
     d'itérations, et un cache serait une source d'incohérence de plus entre le
     dessin et le devis.
     ============================================================ */
  tileOverrides: function (m) {
    const map = new Map();
    if (!this.enabled || !this.items.length) return map;
    const col = m.collections[m.options.currentCollection];
    if (!col) return map;
    const cols = m.gridCols ? m.gridCols() : Math.ceil(m.options.width / m.options.mosaikWidth);
    const rows = (m.layout && m.layout.rows) || Math.ceil(m.options.height / m.options.mosaikHeight);
    this.items.forEach(it => {
      if (it.family !== 'frame') return;
      if (this.drag && this.drag.item === it && this.drag.moved) return;   // il est dans la main
      const pile = col.piles[it.pile] || col.piles[this.defaultPile(col)];
      if (!pile) return;
      this.ringTiles(m, it, cols, rows).forEach(idx => map.set(idx, pile));
    });
    return map;
  },

  /* Couleur de départ d'un encadrement : la plus CLAIRE de la gamme (un blanc
     s'il y en a un), parce qu'un sol de garage est presque toujours sombre et
     qu'un contour doit se voir. */
  defaultPile: function (col) {
    const piles = col.piles || [];
    let i = piles.findIndex(p => /blanc|white/i.test((p.name_fr || '') + ' ' + (p.name_en || '')));
    if (i < 0) i = piles.findIndex(p => /jaune|yellow/i.test((p.name_fr || '') + ' ' + (p.name_en || '')));
    if (i < 0) i = piles.length - 1;
    return Math.max(0, i);
  },

  /* ============================================================
     DESSIN DANS LE PLAN  (appelé par mosaik-render.js)
     Les encadrements ne passent PAS par ici : ce sont des dalles, et elles ont
     déjà été peintes par le rendu à partir de la table de substitution. Il ne
     reste donc que les luminaires, qui ne sont pas du sol.
     ============================================================ */
  draw: function (m, layout) {
    if (!this.enabled || !this.items.length) return;
    const g = this.metrics(m);
    if (!g) return;
    this.items.forEach(it => {
      if (it.family !== 'light') return;
      if (this.drag && this.drag.item === it && this.drag.moved) return;   // il est dans la main
      this.drawFixture(m.ctx, m, it, 1);
    });
  },

  /* Le contour d'un encadrement, dalle par dalle, dans SA couleur. Le plan le
     peint normalement lui-même (table de substitution), mais deux autres
     endroits en ont besoin sur un calque : l'aperçu du glissé, et le filet de
     déploiement ci-dessous. `alpha` laisse alors deviner la dalle en dessous —
     c'est un aperçu, pas encore le sol. */
  drawRing: function (ctx, m, item, alpha) {
    const g = this.metrics(m);
    if (!g) return;
    const col = m.collections[m.options.currentCollection] || {};
    const piles = col.piles || [];
    const pile = piles[item.pile] || piles[this.defaultPile(col)];
    const cols = m.gridCols ? m.gridCols() : Math.ceil(m.options.width / m.options.mosaikWidth);
    const rows = (m.layout && m.layout.rows) || 0;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = (pile && pile.color) || '#fff';
    ctx.strokeStyle = 'rgba(255,255,255,.85)';
    ctx.lineWidth = 1;
    this.ringTiles(m, item, cols, rows).forEach(idx => {
      const c = idx % cols, r = Math.floor(idx / cols);
      const x = g.L.xOff + c * g.L.tileW, y = g.L.yOff + r * g.L.tileH;
      ctx.fillRect(x, y, g.L.tileW, g.L.tileH);
      ctx.strokeRect(x, y, g.L.tileW, g.L.tileH);
    });
    ctx.restore();
  },

  /* Un luminaire vu de dessus : le halo qu'il projette au sol, puis les tubes —
     et le cadre rectangulaire des modèles qui en ont un, tracé du même tube.
     `alpha` sert à l'aperçu du glissé, dessiné plus transparent. */
  drawFixture: function (ctx, m, item, alpha) {
    const g = this.metrics(m);
    if (!g) return;
    const d = this.def(item), comb = this.combOf(d);
    if (!comb) return;
    const { L, mx, my } = g;
    const x0 = L.xOff + item.col * L.tileW, y0 = L.yOff + item.row * L.tileH;
    const tube = Math.max(2, this.HEX_TUBE * mx);
    const pts = this.hexPoints(item.rot, comb.hw, comb.hh);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(x0, y0);
    const path = c => {                        // le contour d'un hexagone allongé
      const p = new Path2D();
      pts.forEach((v, k) => {
        const px = c.x + v.x * mx, py = c.y + v.y * my;
        k ? p.lineTo(px, py) : p.moveTo(px, py);
      });
      p.closePath();
      return p;
    };
    const centers = this.hexCenters(item).map(c => ({ x: c.x * mx, y: c.y * my }));
    // le cadre : un rectangle de tube autour de tout le nid d'abeilles
    const size = this.sizeM(item);
    const framePath = comb.frame ? (() => {
      const p = new Path2D();
      const inset = tube / 2;
      p.rect(inset, inset, size.w * mx - tube, size.h * my - tube);
      return p;
    })() : null;

    // 1. le halo : ce que le luminaire éclaire, pas le luminaire lui-même
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = 'rgba(214,232,255,0.16)';
    centers.forEach(c => ctx.fill(path(c)));
    ctx.globalCompositeOperation = 'source-over';
    // 2. les tubes : un liseré bleuté (la diffusion) sous un trait blanc franc
    const tubeOf = p => {
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(150,200,255,0.55)'; ctx.lineWidth = tube * 2.1; ctx.stroke(p);
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = tube; ctx.stroke(p);
      ctx.strokeStyle = 'rgba(120,140,160,0.55)'; ctx.lineWidth = 1; ctx.stroke(p);
    };
    centers.forEach(c => tubeOf(path(c)));
    if (framePath) tubeOf(framePath);
    ctx.restore();
  },

  /* ============================================================
     LE CALQUE DE RECOUVREMENT  (poignées, sélection, aperçu du glissé)
     Même procédé que mosaik-select.js : un <canvas> posé sur le plan, qui en
     recopie la boîte ET la transformation CSS, donc suit le zoom et le
     déplacement sans rien savoir d'eux. Marqué hors-PDF.
     ============================================================ */
  _buildOverlay: function () {
    if (!this.frame || this.ov) return;
    const ov = document.createElement('canvas');
    ov.id = 'mosaik-objects-ov';
    ov.setAttribute('data-html2canvas-ignore', 'true');
    ov.style.cssText = 'position:absolute; left:0; top:0; pointer-events:none; z-index:5; transform-origin:0 0;';
    this.frame.appendChild(ov);
    this.ov = ov;
    this.octx = ov.getContext('2d');
  },

  render: function () {
    const ov = this.ov, cv = this.cv, m = this.m;
    if (!ov || !cv || !m) return;
    const w = cv.offsetWidth, h = cv.offsetHeight,
      bx = cv.clientLeft, by = cv.clientTop;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (ov.width !== Math.round(w * dpr) || ov.height !== Math.round(h * dpr)) {
      ov.width = Math.round(w * dpr); ov.height = Math.round(h * dpr);
    }
    ov.style.left = cv.offsetLeft + 'px';
    ov.style.top = cv.offsetTop + 'px';
    ov.style.width = w + 'px';
    ov.style.height = h + 'px';
    ov.style.transform = cv.style.transform;

    const ctx = this.octx;
    ctx.setTransform(dpr, 0, 0, dpr, bx * dpr, by * dpr);
    ctx.clearRect(-bx, -by, w, h);
    if (!this.enabled || !this.metrics(m)) return;

    /* ---- filet de déploiement : LES OBJETS, SI LE PLAN NE LES A PAS PEINTS ----
       Un objet posé se voit normalement DANS le plan : le contour est fait de
       dalles recolorées, et les luminaires sont dessinés par le rendu. Les deux
       demandent un mosaik-render2.js (et un mosaik-core2.js) de cette
       génération-ci — or les assets partent fichier par fichier, et un thème
       peut n'avoir reçu que celui-ci. On voyait alors les poignées d'un objet
       invisible, ce qui n'apprend rien.

       Faute de mieux, on le dessine ici, sur le calque, par transparence : le
       visiteur voit ce qu'il pose. Le devis et le PDF, eux, ne l'auront pas —
       ils se nourrissent du plan, pas de ce calque. C'est un pansement le temps
       du déploiement, pas un mode de fonctionnement. */
    if (!m._objectsPainted) {
      this.items.forEach(it => {
        if (this.drag && this.drag.item === it && this.drag.moved) return;
        if (it.family === 'light') this.drawFixture(ctx, m, it, 0.95);
        else this.drawRing(ctx, m, it, 0.8);
      });
    }

    // les pastilles de prise : discrètes, mais toujours là — c'est le seul
    // repère qui dise « ceci est un objet, il se déplace »
    this.items.forEach(it => {
      if (this.drag && this.drag.item === it && this.drag.moved) return;
      this.drawGrip(ctx, m, it, it.id === this.sel);
    });

    // l'objet sélectionné : son cadre et ses deux poignées
    const s = this.selected();
    if (s && !(this.drag && this.drag.item === s && this.drag.moved)) this.drawSelection(ctx, m, s);

    // l'aperçu de ce qui est dans la main
    if (this.drag && this.drag.moved && this.drag.preview) this.drawPreview(ctx, m, this.drag);
  },

  drawGrip: function (ctx, m, item, on) {
    const b = this.boxOf(m, item);
    if (!b) return;
    const x = b.x + b.w / 2, y = b.y + b.h / 2, r = this.GRIP_R;
    ctx.save();
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = on ? 'rgba(105,155,180,.95)' : 'rgba(255,255,255,.88)';
    ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = on ? '#fff' : '#699bb4'; ctx.stroke();
    // la croix de déplacement, quatre traits
    ctx.strokeStyle = on ? '#fff' : '#699bb4'; ctx.lineWidth = 1.6; ctx.lineCap = 'round';
    const a = r * 0.5;
    ctx.beginPath();
    ctx.moveTo(x - a, y); ctx.lineTo(x + a, y);
    ctx.moveTo(x, y - a); ctx.lineTo(x, y + a);
    ctx.stroke();
    ctx.restore();
  },

  /* Les deux poignées de l'objet sélectionné, posées sur les coins hauts de sa
     boîte : « tourner » à gauche, « retirer » à droite. */
  handlesOf: function (m, item) {
    const b = this.boxOf(m, item);
    if (!b) return null;
    return { rotate: { x: b.x, y: b.y }, remove: { x: b.x + b.w, y: b.y }, box: b };
  },

  drawSelection: function (ctx, m, item) {
    const H = this.handlesOf(m, item);
    if (!H) return;
    const b = H.box;
    ctx.save();
    // le cadre, doublé d'un liseré blanc : le sol est le plus souvent noir
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 3.5;
    ctx.strokeRect(b.x + .5, b.y + .5, b.w - 1, b.h - 1);
    ctx.strokeStyle = '#699bb4'; ctx.lineWidth = 1.6;
    ctx.strokeRect(b.x + .5, b.y + .5, b.w - 1, b.h - 1);
    ctx.setLineDash([]);
    ctx.restore();
    this.drawHandle(ctx, H.rotate.x, H.rotate.y, 'rotate');
    this.drawHandle(ctx, H.remove.x, H.remove.y, 'remove');
  },

  drawHandle: function (ctx, x, y, kind) {
    const r = this.HANDLE_R;
    ctx.save();
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = (kind === 'remove') ? '#d9534f' : '#699bb4';
    ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = '#fff'; ctx.stroke();
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (kind === 'remove') {
      const a = r * 0.42;
      ctx.beginPath();
      ctx.moveTo(x - a, y - a); ctx.lineTo(x + a, y + a);
      ctx.moveTo(x + a, y - a); ctx.lineTo(x - a, y + a);
      ctx.stroke();
    } else {
      const a = r * 0.5;
      ctx.beginPath();
      ctx.arc(x, y, a, Math.PI * 0.6, Math.PI * 2.1);      // la flèche circulaire
      ctx.stroke();
      ctx.beginPath();                                      // sa pointe
      ctx.moveTo(x + a * 0.15, y - a * 1.15);
      ctx.lineTo(x + a * 0.62, y - a * 0.62);
      ctx.lineTo(x - a * 0.10, y - a * 0.40);
      ctx.closePath();
      ctx.fillStyle = '#fff'; ctx.fill();
    }
    ctx.restore();
  },

  /* L'aperçu du glissé : l'objet tel qu'il sera posé, à la dalle près, en vert
     s'il tient sur le plan et en rouge sinon. */
  drawPreview: function (ctx, m, drag) {
    const p = drag.preview;
    const ghost = { family: p.family, key: p.key, col: p.col, row: p.row, rot: p.rot, pile: p.pile };
    const b = this.boxOf(m, ghost);
    if (!b) return;
    const ok = p.fits;
    ctx.save();
    ctx.fillStyle = ok ? 'rgba(34,164,93,.16)' : 'rgba(217,83,79,.18)';
    ctx.fillRect(b.x, b.y, b.w, b.h);
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 2; ctx.strokeStyle = ok ? '#22a45d' : '#d9534f';
    ctx.strokeRect(b.x + 1, b.y + 1, b.w - 2, b.h - 2);
    ctx.setLineDash([]);
    ctx.restore();
    if (ghost.family === 'light') this.drawFixture(ctx, m, ghost, 0.75);
    else this.drawRing(ctx, m, ghost, 0.85);   // les dalles que le contour prendra
  },

  /* ============================================================
     VISÉE  (tout en pixels internes du plan)
     ============================================================ */

  selected: function () { return this.items.find(it => it.id === this.sel) || null; },

  /* La poignée de l'objet sélectionné sous le point, s'il y en a une. */
  hitHandle: function (x, y) {
    const s = this.selected();
    if (!s) return null;
    const H = this.handlesOf(this.m, s);
    if (!H) return null;
    const near = (p) => Math.hypot(x - p.x, y - p.y) <= this.HANDLE_R + this.HIT_PAD;
    if (near(H.remove)) return 'remove';
    if (near(H.rotate)) return 'rotate';
    return null;
  },

  /* L'objet sous le point, s'il y en a un. Les DERNIERS POSÉS RÉPONDENT
     D'ABORD : c'est celui qu'on voit au-dessus, donc celui qu'on vise. */
  hitItem: function (x, y) {
    for (let i = this.items.length - 1; i >= 0; i--) {
      if (this.hitOne(this.items[i], x, y)) return this.items[i];
    }
    return null;
  },

  /* Un objet se prend PAR SON TRACÉ, pas par sa surface : le milieu d'un
     emplacement de voiture doit rester peignable. La pastille centrale est la
     seule exception — c'est la prise franche, celle qu'on vise au doigt. */
  hitOne: function (item, x, y) {
    const b = this.boxOf(this.m, item);
    if (!b) return false;
    if (Math.hypot(x - (b.x + b.w / 2), y - (b.y + b.h / 2)) <= this.GRIP_R + this.HIT_PAD) return true;
    const pad = this.HIT_PAD;
    if (x < b.x - pad || x > b.x + b.w + pad || y < b.y - pad || y > b.y + b.h + pad) return false;
    if (item.family === 'frame') {
      const g = this.metrics(this.m);
      if (!g) return false;
      const inx = b.x + g.L.tileW, iny = b.y + g.L.tileH;          // le trou du contour
      const inw = b.w - 2 * g.L.tileW, inh = b.h - 2 * g.L.tileH;
      const inside = (x > inx && x < inx + inw && y > iny && y < iny + inh);
      return !inside;
    }
    /* Luminaire : on le prend SUR UN TUBE — dans un hexagone mais pas dans son
       creux — ou sur son cadre. L'intérieur des hexagones reste donc peignable,
       comme le milieu d'un emplacement.

       Test analytique de l'hexagone allongé (demi-cotes hw, hh, pointes à
       gauche et à droite) : le point est dedans si |dy| ≤ hh ET, sous l'arête
       oblique, |dy| ≤ 2·hh·(hw − |dx|)/hw. Le creux est le même hexagone rentré
       de l'épaisseur du tube. */
    const g = this.metrics(this.m);
    if (!g) return false;
    const d = this.def(item), comb = this.combOf(d);
    if (!comb) return false;
    const halfM = this.turn(item.rot, comb.hw, comb.hh);
    const hw = halfM.x * g.mx, hh = halfM.y * g.my;
    const tube = Math.max(3, this.HEX_TUBE * g.mx) + pad;
    const inHex = (dx, dy, a, b2) => a > 0 && b2 > 0
      && Math.abs(dx) <= a && Math.abs(dy) <= b2
      && Math.abs(dy) <= 2 * b2 * (a - Math.abs(dx)) / a;
    const onComb = this.hexCenters(item).some(c => {
      const dx = x - (b.x + c.x * g.mx), dy = y - (b.y + c.y * g.my);
      return inHex(dx, dy, hw, hh) && !inHex(dx, dy, hw - tube * 1.2, hh - tube * 1.2);
    });
    if (onComb || !comb.frame) return onComb;
    // le cadre : la bande de son pourtour
    const t = tube * 1.2;
    return x <= b.x + t || x >= b.x + b.w - t || y <= b.y + t || y >= b.y + b.h - t;
  },

  /* ============================================================
     POSE ET DÉPLACEMENT
     ============================================================ */

  /* Coin haut-gauche calé sur la grille, pour un objet dont le CENTRE est en
     (x, y) pixels. Un encadrement se cale à la dalle (son contour est fait de
     dalles) ; un luminaire, au plafond, se cale à la demi-dalle — assez pour
     l'aligner sur le sol, assez fin pour le centrer entre deux rangées. */
  snapAt: function (item, x, y) {
    const g = this.metrics(this.m);
    if (!g) return null;
    const { L } = g, t = this.sizeT(this.m, item);
    let col = (x - L.xOff) / L.tileW - t.wT / 2,
      row = (y - L.yOff) / L.tileH - t.hT / 2;
    /* Un encadrement doit tenir ENTIÈREMENT sur le plan : plutôt que de refuser
       la pose près d'un bord, on le rentre. Le geste garde ainsi le même
       résultat qu'on vise le milieu d'une dalle ou son coin, et le refus est
       réservé au seul cas où il ne tient pas du tout (plan trop petit). */
    if (item.family === 'frame') {
      col = Math.max(0, Math.min(col, L.cols - t.wT));
      row = Math.max(0, Math.min(row, L.rows - t.hT));
    }
    const step = (item.family === 'frame') ? 1 : 0.5;
    return { col: Math.round(col / step) * step, row: Math.round(row / step) * step };
  },

  /* L'objet tient-il sur le plan ? Un encadrement doit y tenir ENTIÈREMENT
     (un contour tronqué ne veut rien dire) ; un luminaire est au plafond, il
     lui suffit d'être au-dessus du sol dessiné. */
  fits: function (item, col, row) {
    const m = this.m, L = m.layout;
    if (!L) return false;
    const t = this.sizeT(m, { ...item, col, row });
    if (item.family === 'frame')
      return col >= 0 && row >= 0 && col + t.wT <= L.cols && row + t.hT <= L.rows;
    return col + t.wT > 0.25 && row + t.hT > 0.25 && col < L.cols - 0.25 && row < L.rows - 0.25;
  },

  /* ============================================================
     LE GESTE  (souris et doigt passent par les mêmes quatre fonctions)
     Les coordonnées entrantes sont CELLES DE L'ÉCRAN : la conversion vers les
     pixels du plan est faite ici, transformation du zoom comprise.
     ============================================================ */

  _toInternal: function (clientX, clientY) {
    const cv = this.cv, r = cv.getBoundingClientRect();
    return {
      x: (clientX - r.left) / r.width * cv.clientWidth,
      y: (clientY - r.top) / r.height * cv.clientHeight
    };
  },

  /* Le plan est-il sous ce point ? (une dépose lâchée à côté est annulée) */
  overPlan: function (clientX, clientY) {
    if (!this.frame) return false;
    const r = this.frame.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  },

  /* Le contrôleur et la couche tactile demandent ceci avant de peindre : tant
     qu'un objet est armé, pris ou vient d'être manipulé, le plan ne se peint
     pas — le clic appartient à l'objet. */
  blocksPaint: function () { return !!(this.enabled && (this.armed || this.drag || this._acted)); },

  /* Un clic vient d'être consommé par cette couche.

     LE VERROU DOIT SURVIVRE JUSQU'AU `click`. Le navigateur l'émet à la fin du
     geste, donc APRÈS le mouseup — et c'est lui, pas le mousedown, que le
     contrôleur écoute pour peindre. Un simple setTimeout(0) serait retombé bien
     avant, et prendre un objet aurait posé une dalle dessous.

     On lâche donc le verrou sur ce clic-là, en phase REMONTANTE (le
     gestionnaire du plan s'exécute avant, et voit encore le verrou posé), avec
     un délai de sûreté pour le geste qui n'émet aucun clic — au doigt, ou
     relâché hors de la page. */
  _consume: function () {
    this._acted = true;
    const self = this;
    const done = () => {
      self._acted = false;
      window.removeEventListener('click', done);
      clearTimeout(self._actT);
    };
    window.addEventListener('click', done);
    clearTimeout(this._actT);
    this._actT = setTimeout(done, 700);
  },

  /* Début d'un geste SUR LE PLAN. Rend true si la couche prend la main. */
  begin: function (clientX, clientY) {
    if (!this.enabled || !this.m || !this.m.layout) return false;
    const p = this._toInternal(clientX, clientY);

    // 1. une vignette armée par une tape attend d'être posée
    if (this.armed) {
      const proto = {
        family: this.armed.family, key: this.armed.key, rot: 0,
        pile: this.armed.family === 'frame' ? this.currentPileIdx() : null
      };
      const s = this.snapAt(proto, p.x, p.y);
      if (s) this.place(proto, s.col, s.row);
      this.setArmed(null);
      this._consume();
      return true;
    }

    // 2. une poignée de l'objet sélectionné
    const h = this.hitHandle(p.x, p.y);
    if (h) {
      if (h === 'remove') this.remove(this.sel);
      else this.rotate(this.sel);
      this._consume();
      return true;
    }

    // 3. un objet : on le sélectionne, et le geste peut devenir un déplacement
    const it = this.hitItem(p.x, p.y);
    if (it) {
      this.select(it.id);
      this.drag = {
        item: it, from: { col: it.col, row: it.row }, x0: clientX, y0: clientY,
        // décalage entre le point saisi et le centre de l'objet : sans
        // lui, l'objet sauterait sous le curseur au premier pixel
        grab: this._grabOffset(it, p), moved: false, preview: null
      };
      this._consume();
      return true;
    }

    // 4. rien sous le point : on relâche la sélection, mais on LAISSE PEINDRE —
    //    un clic sur le sol reste un coup de pinceau.
    if (this.sel !== null) { this.select(null); }
    return false;
  },

  _grabOffset: function (item, p) {
    const b = this.boxOf(this.m, item);
    if (!b) return { dx: 0, dy: 0 };
    return { dx: b.x + b.w / 2 - p.x, dy: b.y + b.h / 2 - p.y };
  },

  move: function (clientX, clientY) {
    const d = this.drag;
    if (!d) return;
    if (!d.moved && Math.hypot(clientX - d.x0, clientY - d.y0) < this.DRAG_MIN) return;
    if (!d.moved) {
      d.moved = true;
      // l'objet quitte le plan pour la main : il est retiré du dessin (la table
      // de substitution et le calque des luminaires l'ignorent désormais), donc
      // un seul vrai tracé au départ du glissé, aucun ensuite.
      if (d.item) this.m.draw();
    }
    const p = this._toInternal(clientX, clientY);
    const ref = d.item || d.proto;
    const s = this.snapAt(ref, p.x + (d.grab ? d.grab.dx : 0), p.y + (d.grab ? d.grab.dy : 0));
    if (!s) return;
    d.preview = {
      family: ref.family, key: ref.key, col: s.col, row: s.row, rot: ref.rot || 0,
      pile: ref.pile, fits: this.fits(ref, s.col, s.row) && this.overPlan(clientX, clientY)
    };
    this.render();
    if (this.ghost) this._moveGhost(clientX, clientY);
  },

  end: function (clientX, clientY) {
    const d = this.drag;
    this.drag = null;
    this._killGhost();
    /* Le clic de fin de geste ne doit toujours pas peindre (voir _consume) : le
       verrou est reposé ici, car un glissé dure plus longtemps que son délai de
       sûreté et l'aurait laissé retomber en cours de route.

       SEULEMENT POUR UN GESTE PARTI DU PLAN. Une vignette lâchée depuis la barre
       latérale, elle, n'émet aucun clic sur le plan (le pointeur est capturé par
       le bouton, et son pointerdown a annulé les événements souris de
       compatibilité) : le verrou n'aurait alors rien à intercepter, et c'est le
       PROCHAIN clic du visiteur — un vrai coup de pinceau — qu'il avalerait. */
    if (d && d.item) this._consume();
    if (!d) return;
    if (!d.moved) {
      // une tape : rien n'a bougé. Sur une vignette, elle ARME l'article ;
      // sur un objet du plan, elle l'a déjà sélectionné dans begin().
      if (d.proto) this.setArmed({ family: d.proto.family, key: d.proto.key });
      this.render();
      return;
    }
    const p = d.preview;
    if (p && p.fits) {
      if (d.item) {
        d.item.col = p.col; d.item.row = p.row;
        this.commit();
      } else {
        this.place({ family: p.family, key: p.key, rot: p.rot, pile: p.pile }, p.col, p.row);
        return;                                   // place() dessine et retient
      }
    } else if (d.item) {
      d.item.col = d.from.col; d.item.row = d.from.row;   // lâché à côté : il revient
    }
    this.m.draw();
    this.render();
    this.syncUI();
  },

  cancel: function () {
    const d = this.drag;
    this.drag = null;
    this._killGhost();
    if (d && d.item) this._consume();          // idem : voir end()
    if (d && d.item && d.moved) { d.item.col = d.from.col; d.item.row = d.from.row; this.m.draw(); }
    this.setArmed(null);
    this.render();
  },

  /* ---- le doigt (appelé par mosaik-zoom.js) ---- */
  touchBegin: function (clientX, clientY) { return this.begin(clientX, clientY); },
  touchMove: function (clientX, clientY) { this.move(clientX, clientY); },
  touchEnd: function (clientX, clientY) { this.end(clientX, clientY); },

  /* ============================================================
     MUTATIONS
     ============================================================ */
  place: function (proto, col, row) {
    const m = this.m;
    if (!this.fits(proto, col, row)) {
      this.flash(m.getText('This item does not fit on the plan.'));
      return null;
    }
    const it = {
      id: this._nextId++, family: proto.family, key: proto.key,
      col, row, rot: proto.rot || 0,
      pile: proto.family === 'frame'
        ? (proto.pile != null ? proto.pile : this.currentPileIdx()) : null
    };
    this.items.push(it);
    this.sel = it.id;
    this.commit();
    m.draw();
    this.render();
    this.syncUI();
    return it;
  },

  remove: function (id) {
    const i = this.items.findIndex(it => it.id === id);
    if (i < 0) return;
    this.items.splice(i, 1);
    if (this.sel === id) this.sel = null;
    this.commit();
    this.m.draw();
    this.render();
    this.syncUI();
  },

  rotate: function (id) {
    const it = this.items.find(o => o.id === id);
    if (!it) return;
    const rot = it.rot ? 0 : 1;
    // tourné, l'objet peut sortir du plan : on le ramène dedans plutôt que de
    // refuser la rotation — c'est ce que le visiteur attend d'un quart de tour.
    const t = this.sizeT(this.m, { ...it, rot });
    const L = this.m.layout;
    let col = it.col, row = it.row;
    if (L) {
      col = Math.max(0, Math.min(col, L.cols - t.wT));
      row = Math.max(0, Math.min(row, L.rows - t.hT));
    }
    if (!this.fits({ ...it, rot }, col, row)) {
      this.flash(this.m.getText('This item does not fit on the plan.'));
      return;
    }
    it.rot = rot; it.col = col; it.row = row;
    this.commit();
    this.m.draw();
    this.render();
  },

  /* Recolore l'encadrement sélectionné, ou fixe la couleur des suivants. */
  setPileIdx: function (i) {
    this.pileIdx = i;
    const s = this.selected();
    if (s && s.family === 'frame') {
      s.pile = i;
      this.commit();
      this.m.draw();
    }
    this.syncUI();
    this.render();
  },

  currentPileIdx: function () {
    const col = this.m.collections[this.m.options.currentCollection];
    if (!col) return 0;
    if (this.pileIdx != null && col.piles[this.pileIdx]) return this.pileIdx;
    return this.defaultPile(col);
  },

  select: function (id) {
    this.sel = id;
    this.render();
    this.syncUI();
  },

  clear: function (m) {
    this.items = [];
    this.sel = null;
    this.drag = null;
    this.setArmed(null);
    this.render();
    this.syncUI();
  },

  /* Une action est posée : l'historique la retient (mosaik-toolbar.js). */
  commit: function () {
    if (window.MosaikHistory && MosaikHistory.commit) MosaikHistory.commit();
  },

  /* ============================================================
     (DÉ)SÉRIALISATION — projets enregistrés, modèles, historique
     Forme compacte et INDÉPENDANTE DE LA GRILLE : la position est en dalles,
     la couleur en index de gamme. Rien de dérivé n'est stocké.
     ============================================================ */
  serialize: function () {
    return this.items.map(it => ({
      f: it.family, k: it.key, c: it.col, r: it.row,
      rot: it.rot || 0, p: it.pile == null ? null : it.pile
    }));
  },

  load: function (m, arr) {
    this.items = [];
    this.sel = null;
    this._nextId = 1;
    (arr || []).forEach(o => {
      const family = o.f === 'light' ? 'light' : 'frame';
      const list = family === 'frame' ? this.CATALOG.frames : this.CATALOG.lights;
      if (!list.some(d => d.key === o.k)) return;      // article retiré du catalogue
      this.items.push({
        id: this._nextId++, family, key: o.k,
        col: o.c || 0, row: o.r || 0, rot: o.rot ? 1 : 0,
        pile: (o.p == null ? null : o.p)
      });
    });
    this.render();
    this.syncUI();
  },

  /* Restitution par l'historique : comme load(), mais sans toucher au reste de
     l'UI ni redessiner (c'est l'historique qui redessine). */
  restore: function (m, arr) {
    const sel = this.sel;
    this.load(m, arr);
    if (this.items.some(it => it.id === sel)) this.sel = sel;
  },

  /* ============================================================
     PRIX, RÉCAPITULATIF, PANIER
     Les encadrements N'ONT PAS DE PRIX : ils recolorent des dalles déjà
     comptées. Ils apparaissent quand même au récapitulatif, sans montant, pour
     que le devis dise ce qui a été dessiné.
     ============================================================ */
  lightCounts: function () {
    const out = {};
    this.items.forEach(it => {
      if (it.family !== 'light') return;
      out[it.key] = (out[it.key] || 0) + 1;
    });
    return out;
  },

  frameCount: function () { return this.items.filter(it => it.family === 'frame').length; },

  price: function () {
    const counts = this.lightCounts();
    return this.CATALOG.lights.reduce((s, d) => s + (counts[d.key] || 0) * (d.price || 0), 0);
  },

  recapRows: function (m) {
    if (!this.items.length) return '';
    const lang = m.options.lang, poly = m.options.currentCollection === 'Polypropylene';
    const pad = poly ? '<td></td>' : '';
    let html = '';
    const frames = this.frameCount();
    if (frames) {
      html += '<tr><td><b>' + m.getText('Parking spaces') + ':</b></td><td></td><td>'
        + frames + '</td><td></td>' + pad + '</tr>';
    }
    const counts = this.lightCounts();
    const lights = this.CATALOG.lights.filter(d => counts[d.key]);
    if (lights.length) {
      html += '<tr><td><b>' + m.getText('Accessories') + ':</b></td><td></td><td></td><td></td>' + pad + '</tr>';
      lights.forEach(d => {
        const n = counts[d.key], sub = n * (d.price || 0);
        html += '<tr><td></td><td>' + d['name_' + lang] + '</td><td>' + n + '</td><td>'
          + (d.price ? sub.toFixed(2) + ' €' : '') + '</td>' + pad + '</tr>';
      });
    }
    return html;
  },

  addCartItems: function (m, items) {
    const counts = this.lightCounts();
    let warn = false;
    this.CATALOG.lights.forEach(d => {
      const n = counts[d.key];
      if (!n) return;
      if (d.id) items.push({ id: d.id, quantity: n }); else warn = true;
    });
    if (warn) console.warn('[MosaikObjects] Renseignez CATALOG.lights[].id (et .price) '
      + 'pour que les accessoires partent au panier.');
  },

  /* ============================================================
     LA BARRE LATÉRALE
     ============================================================ */

  /* Vignette d'un article : un petit dessin (le contour, ou la chaîne
     d'hexagones) et son nom. Le dessin est construit ici plutôt que d'être une
     image à déployer — il suit le catalogue, toujours. */
  chipSvg: function (family, def) {
    const W = 62, H = 40;
    if (family === 'frame') {
      const r = def.w / def.h;                       // le rapport réel de l'emplacement
      const h = H - 6, w = Math.max(10, Math.min(W - 6, h * r));
      const x = (W - w) / 2, y = (H - h) / 2;
      return '<svg viewBox="0 0 ' + W + ' ' + H + '" aria-hidden="true">'
        + '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '"'
        + ' fill="none" stroke="currentColor" stroke-width="3"/></svg>';
    }
    /* Luminaire : LA VIGNETTE EST LE PRODUIT, à l'échelle près. Elle est
       construite à partir du MÊME nid d'abeilles que le tracé sur le plan
       (combOf / hexPoints), cadre compris — un modèle sans cadre est donc
       exactement le même dessin sans son rectangle. Rien à redessiner à la main
       ici : ajouter un article au catalogue suffit à obtenir sa vignette. */
    const comb = this.combOf(def);
    if (!comb) return '';
    const pts = this.hexPoints(0, comb.hw, comb.hh);
    const shapes = comb.cells.map(c =>
      '<polygon points="'
      + pts.map(v => (c.x + v.x).toFixed(3) + ',' + (c.y + v.y).toFixed(3)).join(' ')
      + '" fill="none" stroke="currentColor" stroke-width="' + this.HEX_TUBE.toFixed(3) + '"'
      + ' stroke-linejoin="round"/>');
    if (comb.frame) {
      const i = this.HEX_TUBE / 2;
      shapes.unshift('<rect x="' + i + '" y="' + i + '"'
        + ' width="' + (comb.w - this.HEX_TUBE).toFixed(3) + '"'
        + ' height="' + (comb.h - this.HEX_TUBE).toFixed(3) + '"'
        + ' fill="none" stroke="currentColor" stroke-width="' + this.HEX_TUBE.toFixed(3) + '"/>');
    }
    // la boîte du SVG est le luminaire lui-même : la vignette garde donc les
    // proportions réelles du produit, telles que le catalogue les donne
    const pad = this.HEX_TUBE;
    return '<svg viewBox="' + (-pad) + ' ' + (-pad) + ' '
      + (comb.w + 2 * pad).toFixed(3) + ' ' + (comb.h + 2 * pad).toFixed(3) + '"'
      + ' aria-hidden="true">' + shapes.join('') + '</svg>';
  },

  buildPalette: function () {
    const m = this.m, self = this;
    const lang = m.options.lang;
    const fill = (hostId, family, list) => {
      const host = document.getElementById(hostId);
      if (!host) return;
      host.innerHTML = '';
      list.forEach(d => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'mosaik-obj-chip';
        b.setAttribute('data-family', family);
        b.setAttribute('data-key', d.key);
        /* Sous le nom : les COTES RÉELLES du catalogue, et le prix pour ce qui
           se vend en plus. L'infobulle y ajoute le poids, qui n'a pas sa place
           sur une vignette de deux centimètres mais compte pour qui prépare une
           pose au plafond. */
        const n = v => v.toFixed(2).replace('.', ',');
        const size = n(d.w) + ' × ' + n(d.h) + ' m'
                   + (d.price ? ' · ' + d.price.toFixed(0) + ' €' : '');
        b.innerHTML = '<span class="mosaik-obj-thumb">' + self.chipSvg(family, d) + '</span>'
          + '<span class="mosaik-obj-name">' + d['name_' + lang] + '</span>'
          + '<span class="mosaik-obj-size">' + size + '</span>';
        b.title = d['name_' + lang] + ' — ' + size
          + (d.weight ? ' · ' + n(d.weight).replace(',00', '') + ' kg' : '');
        self._wireChip(b, family, d);
        host.appendChild(b);
      });
    };
    fill('mosaik-objects-frames', 'frame', this.CATALOG.frames);
    fill('mosaik-objects-lights', 'light', this.CATALOG.lights);
    this.buildColors();
  },

  /* Le nuancier des encadrements : les mêmes pastilles que partout ailleurs
     (.mosaik-swatch), reconstruites à chaque changement de gamme. */
  buildColors: function () {
    const host = document.getElementById('mosaik-objects-colors');
    if (!host || !this.m) return;                 // markup absent, ou appel avant setupUI
    const m = this.m, self = this;
    const col = m.collections[m.options.currentCollection];
    host.innerHTML = '';
    if (!col) return;
    const cur = this.currentPileIdx();
    (col.piles || []).forEach((p, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mosaik-swatch' + (i === cur ? ' sel' : '');
      const dot = document.createElement('span');
      dot.className = 'mosaik-swatch-dot';
      dot.style.background = p.color || '#ccc';
      const lbl = document.createElement('span');
      lbl.className = 'mosaik-swatch-lbl';
      lbl.innerText = m.pileShortName ? m.pileShortName(p) : '';
      btn.appendChild(dot); btn.appendChild(lbl);
      btn.title = m.pileName ? m.pileName(p) : '';
      btn.addEventListener('click', () => {
        host.querySelectorAll('.mosaik-swatch').forEach(el => el.classList.remove('sel'));
        btn.classList.add('sel');
        self.setPileIdx(i);
      });
      host.appendChild(btn);
    });
  },

  /* Une vignette se prend au doigt comme à la souris : les événements pointeur
     couvrent les deux. `touch-action:none` (CSS) est indispensable — sans lui
     le navigateur fait défiler la barre latérale au lieu de nous donner le
     glissé. Une tape (moins de DRAG_MIN px) ARME l'article : sur un téléphone,
     la barre latérale et le plan ne sont pas toujours à l'écran ensemble. */
  _wireChip: function (btn, family, def) {
    const self = this;
    btn.addEventListener('pointerdown', e => {
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      if (btn.setPointerCapture) { try { btn.setPointerCapture(e.pointerId); } catch (err) { } }
      self.setArmed(null);
      const proto = {
        family, key: def.key, rot: 0,
        pile: family === 'frame' ? self.currentPileIdx() : null
      };
      self.drag = { proto, x0: e.clientX, y0: e.clientY, grab: { dx: 0, dy: 0 }, moved: false, preview: null };
      self._makeGhost(btn, e.clientX, e.clientY);
    });
    // le pointeur est capturé par la vignette : c'est elle qui reçoit le reste
    // du geste, où qu'il aille dans la page (le plan, en l'occurrence)
    btn.addEventListener('pointermove', e => {
      if (self.drag && self.drag.proto) self.move(e.clientX, e.clientY);
    });
    btn.addEventListener('pointerup', e => {
      if (self.drag && self.drag.proto) self.end(e.clientX, e.clientY);
    });
    btn.addEventListener('pointercancel', () => { if (self.drag && self.drag.proto) self.cancel(); });
  },

  /* Le fantôme qui suit le pointeur pendant qu'on traverse la page. Il n'est
     là que pour le trajet : une fois sur le plan, c'est l'aperçu calé sur la
     grille qui dit la vérité. */
  _makeGhost: function (btn, x, y) {
    this._killGhost();
    const g = document.createElement('div');
    g.className = 'mosaik-obj-ghost';
    g.innerHTML = btn.innerHTML;
    g.style.cssText = 'position:fixed; left:0; top:0; z-index:10050; pointer-events:none;'
      + 'opacity:.9; transform:translate(-50%,-50%);';
    document.body.appendChild(g);
    this.ghost = g;
    this._moveGhost(x, y);
  },
  _moveGhost: function (x, y) {
    if (this.ghost) this.ghost.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%)';
  },
  _killGhost: function () {
    if (this.ghost && this.ghost.parentNode) this.ghost.parentNode.removeChild(this.ghost);
    this.ghost = null;
  },

  /* Un article armé attend le plan : la vignette reste allumée et le plan
     prend le curseur « copie ». */
  setArmed: function (a) {
    this.armed = a;
    document.querySelectorAll('#mosaik-card-objects .mosaik-obj-chip').forEach(b => {
      const on = !!a && b.getAttribute('data-family') === a.family && b.getAttribute('data-key') === a.key;
      b.classList.toggle('sel', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this.frame) this.frame.classList.toggle('mosaik-obj-arming', !!a);
    this.syncUI();
  },

  /* Un mot sous la palette, le temps de le lire. Sert aux refus (« ça ne tient
     pas sur le plan ») : une alerte modale pour ça serait hors de proportion. */
  flash: function (msg) {
    const el = document.getElementById('mosaik-objects-status');
    if (!el) return;
    el.innerText = msg;
    el.classList.add('mosaik-obj-warn');
    clearTimeout(this._flash);
    const self = this;
    this._flash = setTimeout(() => { el.classList.remove('mosaik-obj-warn'); self.syncUI(); }, 2600);
  },

  syncUI: function () {
    const m = this.m;
    if (!m) return;
    const t = s => m.getText(s);
    const el = document.getElementById('mosaik-objects-status');
    if (el && !el.classList.contains('mosaik-obj-warn')) {
      const frames = this.frameCount(),
        lights = this.items.length - frames,
        price = this.price();
      let msg;
      if (this.armed) msg = t('Now tap the plan to drop it.');
      else if (!this.items.length) msg = '';
      else {
        msg = frames + ' ' + t('parking spaces');
        if (this.CATALOG.lights.length) msg += ' · ' + lights + ' ' + t('accessories');
        if (price) msg += ' — ' + price.toFixed(2) + ' €';
      }
      el.innerText = msg;
    }
    const clr = document.getElementById('mosaik-objects-clear');
    if (clr) clr.disabled = !this.items.length;
    const hint = document.getElementById('mosaik-objects-hint');
    if (hint) hint.innerText = t('Drag an item onto the plan. Once dropped, grab it by its outline to move it, and use its handles to turn or remove it.');
    const ct = document.getElementById('mosaik-objects-colors-title');
    if (ct) ct.innerText = t('Parking space colour');
    const ft = document.getElementById('mosaik-objects-frames-title');
    if (ft) ft.innerText = t('Parking spaces');
    const lt = document.getElementById('mosaik-objects-lights-title');
    if (lt) lt.innerText = t('Accessories');
    const cl = document.getElementById('mosaik-objects-clear-label');
    if (cl) cl.innerText = t('Remove all');
  },

  /* ============================================================
     DIAGNOSTIC DE DÉPLOIEMENT

     Cette fonctionnalité s'étale sur plusieurs modules, et les assets d'un
     thème Shopify se téléversent UN PAR UN. Un fichier resté en arrière ne
     provoque aucune erreur : il fait simplement disparaître sa part du travail
     — le contour ne se peint plus, ou le devis ne le compte pas — et il n'y a
     rien à l'écran pour dire lequel manque.

     On le dit donc en clair dans la console, une fois, après le premier tracé.
     Chaque module à jour laisse une trace vérifiable ; les autres sont nommés
     avec la conséquence qu'a leur absence. `MosaikObjects.audit()` refait le
     tour à la demande.
     ============================================================ */
  audit: function (verbose) {
    const m = this.m || window.mosaik;
    if (!m) return null;
    const checks = [
      {
        asset: 'mosaik-core2.js', ok: typeof m.tileOverrides === 'function',
        why: 'la table de substitution des dalles'
      },
      {
        asset: 'mosaik-render2.js', ok: m._objectsPainted === true,
        why: 'le contour peint dans le plan (donc dans le PDF) et les luminaires'
      },
      {
        asset: 'mosaik-recap2.js', ok: m._objectsCounted === true,
        why: 'les dalles de l\'emplacement comptées dans le détail et au panier'
      },
      {
        asset: 'mosaik-controller2.js', ok: this._selfMounted !== true,
        why: 'le pinceau qui ne peint plus sous un objet que l\'on saisit'
      },
      {
        asset: 'mosaik-toolbar2.js', ok: !!(window.MosaikHistory && window.MosaikHistory._objects),
        why: 'annuler / refaire la pose d\'un objet'
      },
      {
        asset: 'mosaik-data2.js', ok: m.getText('Parking spaces') !== 'Parking spaces',
        why: 'les libellés en français'
      }
    ];
    const late = checks.filter(c => !c.ok);
    if (late.length && verbose !== false) {
      console.warn('[MosaikObjects] ' + late.length + ' asset(s) du thème sont encore '
        + 'dans leur version d\'avant les objets déposés. À téléverser :\n'
        + late.map(c => '  • ' + c.asset + ' — sans lui : ' + c.why).join('\n'));
    } else if (verbose === true) {
      console.log('[MosaikObjects] Tous les modules attendus sont à jour.');
    }
    return late.map(c => c.asset);
  },

  /* ============================================================
     CÂBLAGE
     ============================================================ */
  setupUI: function (mosaik) {
    if (!document.getElementById('mosaik-card-objects')) return;   // markup non émis
    this.m = mosaik;
    const cv = mosaik.grid;
    if (!cv) return;
    this.cv = cv;
    this.frame = cv.parentElement;
    this._buildOverlay();
    const self = this;

    this.buildPalette();

    // La souris sur le plan. En phase de CAPTURE : le clic qui prend un objet
    // ne doit pas atteindre le contrôleur, qui peindrait la dalle en dessous.
    cv.addEventListener('mousedown', e => {
      if (e.button !== 0) return;
      if (window.MosaikZoom && MosaikZoom.blocksPaint()) return;    // outil main armé
      if (window.MosaikSelect && MosaikSelect.blocksPaint()) return; // tracé de zone
      if (self.begin(e.clientX, e.clientY)) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* LE CLIC QUI CLÔT LE GESTE, ARRÊTÉ ICI MÊME.
       C'est `click`, et non `mousedown`, qui pose une dalle (mosaik-controller.js).
       Le contrôleur à jour nous demande la permission avant de peindre — mais un
       thème en cours de déploiement peut avoir l'ancien, qui ne connaît pas
       cette couche et poserait une dalle sous chaque objet saisi. On coupe donc
       nous-mêmes : `stopImmediatePropagation` parce que le gestionnaire du
       contrôleur est posé sur CE MÊME élément, où un simple stopPropagation ne
       l'atteindrait pas.
       Le verrou est relâché dans la foulée : ce clic-ci était précisément celui
       qu'il attendait (voir _consume). */
    cv.addEventListener('click', e => {
      if (!self.blocksPaint()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self._acted = false;
      clearTimeout(self._actT);
    }, true);
    window.addEventListener('mousemove', e => { if (self.drag && self.drag.item) self.move(e.clientX, e.clientY); });
    window.addEventListener('mouseup', e => { if (self.drag && self.drag.item) self.end(e.clientX, e.clientY); });

    // Échap relâche tout ; Suppr retire l'objet sélectionné.
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (self.drag) self.cancel();
        else if (self.armed) self.setArmed(null);
        else if (self.sel !== null) self.select(null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && self.sel !== null) {
        const tag = (document.activeElement && document.activeElement.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        self.remove(self.sel);
      }
    });

    // le plan porte une transformation CSS (zoom, déplacement) : le calque la
    // recopie, comme celui de la sélection de zone
    if (window.MutationObserver) {
      new MutationObserver(() => self.render())
        .observe(cv, { attributes: true, attributeFilter: ['style'] });
    }

    const clr = document.getElementById('mosaik-objects-clear');
    if (clr) clr.addEventListener('click', () => {
      if (!self.items.length) return;
      self.clear(mosaik);
      self.commit();
      mosaik.draw();
    });

    this.syncUI();
    this.render();
    /* Le tour d'horizon des modules compagnons, une fois le premier plan tracé
       (les drapeaux qu'il lit sont posés par draw() et par calcResult()). */
    setTimeout(function () { self.audit(); }, 1500);
  }
};

/* ---- accessoires (luminaires) : HORS VENTE par défaut ----
   Le catalogue des luminaires n'est gardé que si le gabarit le demande
   explicitement (`accessories_enabled` → window.MOSAIK_ACCESSORIES_ENABLED).
   Vider la liste suffit à tout couper d'un coup : pas de vignette, rien de
   déposable, rien au récapitulatif, rien au panier, et un projet enregistré
   qui contenait un luminaire le perd au rechargement (load() ignore une clé
   absente du catalogue). Un drapeau absent vaut « hors vente ». */
if (window.MOSAIK_ACCESSORIES_ENABLED !== true) window.MosaikObjects.CATALOG.lights = [];

/* ---- filet de déploiement ----
   La carte est normalement montée par le bootstrap de mosaik-controller2.js.
   Les assets partent FICHIER PAR FICHIER : ce module-ci peut donc arriver sur
   un thème dont le contrôleur est encore l'ancien (ou en cache chez le
   visiteur), qui ne connaît pas MosaikObjects et n'appelle donc rien — la carte
   reste alors vide, sans la moindre erreur pour le dire.

   On se monte donc nous-mêmes si personne ne l'a fait. Le délai zéro est
   indispensable : ce script est chargé AVANT le contrôleur, donc son écouteur
   `DOMContentLoaded` s'exécuterait avant mosaik.init() — c'est-à-dire avant que
   le plan n'existe. Le repousser d'un tour de boucle le place après tous les
   autres écouteurs, contrôleur compris. `m` déjà posé = le contrôleur a fait le
   travail, on ne refait rien. */
document.addEventListener('DOMContentLoaded', function () {
  setTimeout(function () {
    const M = window.MosaikObjects;
    if (!M || M.m) return;
    if (!window.mosaik || !window.mosaik.grid) return;
    // personne ne nous a montés : le contrôleur du thème est donc l'ancien.
    // Le diagnostic (audit) s'en sert pour le nommer.
    M._selfMounted = true;
    M.setupUI(window.mosaik);
  }, 0);
});
